import { Toast, ToastType } from 'js/src/components/common/Toast';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { isError } from 'js/src/types';

type ErrorOptions = {
    captureError?: boolean;
    showToast?: boolean;
    toastType?: ToastType;
    toastMessage?: string;
    toastTitle?: string;
    toastDuration?: number;
};

const DEFAULT_ERROR_OPTIONS: ErrorOptions = {
    captureError: true,
    showToast: true,
    toastType: ToastType.ERROR,
};

export class ClipCreatorError extends Error {
    originalError: unknown;
    options: ErrorOptions;

    constructor(error: unknown, options: ErrorOptions = {}) {
        const message = isError(error) ? error.message : String(error);
        super(message);
        this.originalError = error;
        this.options = {
            ...DEFAULT_ERROR_OPTIONS,
            ...options,
        };
    }
}

const isClipCreatorError = (error: unknown): error is ClipCreatorError => {
    return error instanceof ClipCreatorError;
};

export const handleClipCreatorError = (error: unknown) => {
    if (isClipCreatorError(error)) {
        if (error.options.captureError) {
            ErrorReporter.capture(error.originalError);
        }
        if (error.options.showToast) {
            let message = error.options.toastMessage;
            if (!message) {
                if (typeof error.originalError === 'string') {
                    message = error.originalError;
                } else if (isError(error.originalError)) {
                    message = error.originalError.message;
                }
            }
            const toastTitle = error.options.toastTitle; // default is undefined and handled by the toast library, so no need to set a default
            const toastDuration = error.options.toastDuration; // default is undefined and handled by the toast library, so no need to set a default
            switch (error.options.toastType) {
                case ToastType.INFO:
                    Toast.info(message, toastTitle, undefined, toastDuration);
                    break;
                case ToastType.SUCCESS:
                    Toast.success(message, toastTitle, undefined, toastDuration);
                    break;
                case ToastType.ERROR:
                default:
                    Toast.error(message, toastTitle, undefined, toastDuration);
                    break;
            }
        }
    } else {
        throw error;
    }
};
